/**
 * /login — container. O visual é o painel de login do design novo
 * (entrada/PainelLogin), montado dentro da moldura entrada/TelaDeEntrada
 * (rota de layout em App.jsx). Aqui fica só o fluxo real: AuthContext.login,
 * feedback de servidor lento e a navegação para a 2ª etapa ou o destino.
 *
 * O warm-up do backend (JWKS) que esta tela sempre disparou no mount agora
 * sai da moldura (useStatusDoServidor), que também usa a resposta para a
 * pílula "Operacional" do topo.
 */
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { DEFAULT_PATH } from "../../utils/rotas";
import PainelLogin, {
  ESTADO_LENTO,
  ESTADO_LOGANDO,
  ESTADO_MUITO_LENTO,
  ESTADO_PARADO,
} from "./entrada/PainelLogin";
import { marcaDaEntrada } from "./entrada/marca";

// Bloco LOG2 Camada C (2026-05-25): feedback visual progressivo.
// O cold start pode levar 30-50 s. Sem mensagens intermediárias a pessoa
// clica Entrar e vê "Verificando…" por 30 s sem entender o porquê.
const FEEDBACK_SLOW_MS = 5000; // > 5 s: "Conectando ao servidor…"
const FEEDBACK_VERY_SLOW_MS = 15000; // > 15 s: "Servidor inativo está acordando…"

export default function Login() {
  const { authenticated, login, primeiroAcesso } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [estado, setEstado] = useState(ESTADO_PARADO);
  const [erro, setErro] = useState("");
  const timersRef = useRef([]);

  // Cancela os timers de feedback ao desmontar (evita setState em
  // componente desmontado se a navegação acontecer antes do fim).
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    [],
  );

  if (authenticated && primeiroAcesso) {
    return <Navigate to="/primeiro-acesso" replace />;
  }

  if (authenticated) {
    return <Navigate to={location.state?.from || DEFAULT_PATH} replace />;
  }

  function limparTimers() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }

  async function entrar({ email, password }) {
    setErro("");
    setEstado(ESTADO_LOGANDO);
    timersRef.current.push(
      setTimeout(() => setEstado((s) => (s === ESTADO_LOGANDO ? ESTADO_LENTO : s)), FEEDBACK_SLOW_MS),
      setTimeout(
        () => setEstado((s) => (s === ESTADO_LOGANDO || s === ESTADO_LENTO ? ESTADO_MUITO_LENTO : s)),
        FEEDBACK_VERY_SLOW_MS,
      ),
    );

    try {
      const result = await login({ email, password });

      // Sprint 10 — cenário 2: MFA ativo. O mfa_token vai SÓ no state da
      // navegação (NUNCA no localStorage — é curto e não serve como
      // access_token). O e-mail vai junto para a 2ª etapa mascará-lo.
      if (result?.status === "mfa_required") {
        navigate("/mfa-verify", {
          replace: true,
          state: {
            mfaToken: result.mfaToken,
            primeiroAcesso: result.primeiroAcesso,
            from: location.state?.from,
            email,
          },
        });
        return;
      }

      // Cenários 1 (sem MFA) e 3 (entrou com tokens reais, talvez com
      // enrollment pendente — o banner cuida disso no shell autenticado).
      const session = result?.session || {};
      navigate(session.primeiroAcesso ? "/primeiro-acesso" : location.state?.from || DEFAULT_PATH, {
        replace: true,
      });
    } catch (error) {
      setErro(error?.message || "Não foi possível entrar. Tente novamente.");
    } finally {
      limparTimers();
      setEstado(ESTADO_PARADO);
    }
  }

  return (
    <PainelLogin
      marca={marcaDaEntrada()}
      emailInicial={location.state?.email || ""}
      estado={estado}
      erroServidor={erro}
      onEntrar={entrar}
    />
  );
}
